import type { Dataset, BillingSummary, Customer, ARByCustomer, CreditEvent, Project, PortfolioMix, NiExplain } from "./types";
import type { FanData, MixData } from "./charts";
import { ema } from "./charts";

// ============================================================
// Customer risk scoring — K-028 composite. Used by AR & Customer
// Risk tab and Overview watchlist.
// Inputs: per-customer AR row + customer master + recent credit
// events. Output: 0-100 score and Critical/High/Medium/Low band.
// ============================================================
export type RiskBand = "critical" | "high" | "medium" | "low";

export function scoreRisk(
  a: ARByCustomer,
  c: Customer | undefined,
  events: CreditEvent[]
): { score: number; risk: RiskBand } {
  let s = 0;
  if (a.last_pay_cycle_days != null) {
    if (a.last_pay_cycle_days > 100) s += 35;
    else if (a.last_pay_cycle_days > 75) s += 22;
    else if (a.last_pay_cycle_days > 60) s += 12;
  }
  if (c?.credit_paydex_baseline != null) {
    if (c.credit_paydex_baseline < 60) s += 25;
    else if (c.credit_paydex_baseline < 70) s += 12;
  }
  for (const e of events.filter((e) => e.customer_account === a.account_num)) {
    s += e.severity === "Critical" ? 14 : e.severity === "High" ? 9 : e.severity === "Medium" ? 4 : 1;
  }
  s = Math.min(100, s);
  return { score: s, risk: s >= 75 ? "critical" : s >= 50 ? "high" : s >= 25 ? "medium" : "low" };
}

// ============================================================
// Color palette for mix-shift series (cool, no warm clashes).
// ============================================================
const MIX_COLORS: Record<string, string> = {
  "Clean Room": "#3b82f6",
  "Comm Interiors": "#14b8a6",
  Mep: "#6366f1",
  MEP: "#6366f1",
  Scaffold: "#0ea5e9",
  Other: "#64748b",
  Insulation: "#64748b",
};

// Display-name overrides for raw mix-series keys (internal codes are kept
// stable in the data; only the on-screen label changes). "Other" is the
// generic catch-all repurposed as Insulation — PCG's core trade.
const MIX_DISPLAY: Record<string, string> = {
  Mep: "MEP",
  Other: "Insulation",
};

function colorFor(name: string, idx: number): string {
  const fallback = ["#3b82f6", "#14b8a6", "#6366f1", "#0ea5e9", "#64748b"];
  return MIX_COLORS[name] ?? fallback[idx % fallback.length];
}

// ============================================================
// Billing rollups
// ============================================================
export function billingTotals(billing: BillingSummary[]) {
  let revenue = 0, billed = 0, earned = 0, over = 0, under = 0, retainage = 0, backlog = 0;
  for (const b of billing) {
    revenue += b.current_contract_value;
    billed += b.billed_to_date;
    earned += b.earned_to_date;
    retainage += b.retainage_withheld;
    backlog += Math.max(0, b.current_contract_value - b.earned_to_date);
    if (b.over_under_billed > 0) over += b.over_under_billed;
    else under += b.over_under_billed;
  }
  return { revenue, billed, earned, over, under, net: over + under, retainage, backlog };
}

export function billingByType(billing: BillingSummary[]) {
  const m = new Map<string, { revenue: number; over: number; under: number; n: number; backlog: number }>();
  for (const b of billing) {
    const k = b.project_type_code;
    const r = m.get(k) ?? { revenue: 0, over: 0, under: 0, n: 0, backlog: 0 };
    r.revenue += b.current_contract_value;
    r.backlog += Math.max(0, b.current_contract_value - b.earned_to_date);
    if (b.over_under_billed > 0) r.over += b.over_under_billed;
    else r.under += b.over_under_billed;
    r.n += 1;
    m.set(k, r);
  }
  return m;
}

export function customerLookup(customers: Customer[]) {
  return new Map(customers.map((c) => [c.customer_account, c]));
}

export function dsoOverall(arByCust: ARByCustomer[]): number {
  const totalAr = arByCust.reduce((a, c) => a + c.ar_balance, 0);
  const totalInv = arByCust.reduce((a, c) => a + c.invoiced, 0);
  const dailyRev = totalInv / (24 * 30); // ~24 months at ~30 days/month
  return dailyRev > 0 ? totalAr / dailyRev : 0;
}

export function projectsByCustomer(projects: Project[]) {
  const m = new Map<string, Project[]>();
  for (const p of projects) {
    const arr = m.get(p.customer_account) ?? [];
    arr.push(p);
    m.set(p.customer_account, arr);
  }
  return m;
}

export function billingByCustomer(billing: BillingSummary[]) {
  const m = new Map<string, BillingSummary[]>();
  for (const b of billing) {
    const arr = m.get(b.customer_account) ?? [];
    arr.push(b);
    m.set(b.customer_account, arr);
  }
  return m;
}

// ============================================================
// Mix-shift: convert noisy quarterly data → smoothed MixData
// ============================================================
export function mixToChart(mix: PortfolioMix, smooth = true): MixData {
  // Reorder: Clean Room first (so emphasis works), then standard order.
  const order = ["Clean Room", "Comm Interiors", "Mep", "Scaffold", "Other"];
  const sorted = order
    .map((name) => mix.series.find((s) => s.name === name))
    .filter(Boolean) as PortfolioMix["series"];
  // Append any series not in the canonical order (defensive).
  for (const s of mix.series) {
    if (!sorted.find((x) => x.name === s.name)) sorted.push(s);
  }

  const series = sorted.map((s, idx) => ({
    name: MIX_DISPLAY[s.name] ?? s.name,
    color: colorFor(s.name, idx),
    values: smooth ? ema(s.values, 0.5) : s.values.slice(),
  }));

  // Re-normalize each row to sum to 100 (since EMA breaks the constraint).
  const labels = mix.labels;
  if (smooth) {
    for (let i = 0; i < labels.length; i++) {
      const total = series.reduce((a, s) => a + s.values[i], 0);
      if (total > 0) series.forEach((s) => (s.values[i] = (s.values[i] / total) * 100));
    }
  }

  return { labels, series };
}

// ============================================================
// Synthesize a 24-month net income forecast fan from billing totals.
// Real engagement uses a calibrated model — for the demo we shape a
// plausible series anchored to the actual revenue running rate.
// ============================================================
export function forecastFan(d: Dataset, opts: { history?: number; horizon?: number; ymUnit?: "money" | "pct" } = {}): FanData {
  const history = opts.history ?? 12;
  const horizon = opts.horizon ?? 12;
  const totals = billingTotals(d.billing_summary);
  const monthlyRev = totals.revenue / 24;
  const baseMargin = 0.085; // ~8.5% net margin

  const labels: string[] = [];
  const today = new Date(d.meta.asOf);
  for (let i = -history; i < horizon; i++) {
    const dt = new Date(today.getFullYear(), today.getMonth() + i + 1, 1);
    labels.push(`${dt.toLocaleString("en-US", { month: "short" })}'${String(dt.getFullYear()).slice(2)}`);
  }

  // Generate raw monthly series (with seasonality + drift)
  const raw: number[] = [];
  const total = history + horizon;
  for (let i = 0; i < total; i++) {
    const tIdx = i - history; // 0 = first forecast month
    const seasonality = Math.sin((i / 6) * Math.PI) * 0.06;
    const drift = (i - history) * 0.0015;
    const noise = (Math.sin(i * 1.7) + Math.cos(i * 0.9)) * 0.015;
    raw.push(monthlyRev * (baseMargin + seasonality + drift + (i < history ? noise : 0)));
    void tIdx;
  }
  const smooth = ema(raw, 0.55);

  const actuals = smooth.slice(0, history);
  const forecast = smooth.slice(history);

  // Prediction intervals widen with horizon (sqrt(t))
  const piWidthPct = (i: number) => 0.08 + 0.018 * Math.sqrt(i + 1);
  const fc50U: number[] = [], fc50L: number[] = [];
  const fc80U: number[] = [], fc80L: number[] = [];
  const fc95U: number[] = [], fc95L: number[] = [];
  forecast.forEach((v, i) => {
    const w = piWidthPct(i);
    fc50U.push(v * (1 + w * 0.4)); fc50L.push(v * (1 - w * 0.4));
    fc80U.push(v * (1 + w * 0.7)); fc80L.push(v * (1 - w * 0.7));
    fc95U.push(v * (1 + w));       fc95L.push(v * (1 - w));
  });

  // D365 baseline: slightly conservative + persistent downward drift
  const d365 = forecast.map((v, i) => v * (0.93 - i * 0.005));

  return { labels, actuals, forecast, fc50U, fc50L, fc80U, fc80L, fc95U, fc95L, d365 };
}

// ============================================================
// Net income forecast — Holt-Winters on PUBLIC SEC data.
// Replaces the synthetic forecastFan for the net-income surface.
// Reads the exported ni_forecast.json (data.ni_forecast); falls
// back to the synthetic fan only if that file is absent.
// horizonQuarters trims the forward horizon (2 / 4 / 8).
// ============================================================
export interface NiFanResult extends FanData {
  company: string;
  companyName: string;
  model: string;
  source: string;
  accuracy: { qMAPE: number | null; annualMAPE: number | null; mae: number | null; rmse: number | null; mase: number | null; pi80Coverage: number | null };
  explain: NiExplain | null;
  available: boolean;
}

export function netIncomeForecastFan(
  d: Dataset,
  opts: { company?: string; horizonQuarters?: number } = {},
): NiFanResult {
  const horizon = opts.horizonQuarters ?? 8;
  const nf = d.ni_forecast;
  if (!nf) {
    // graceful fallback to the synthetic fan (keeps the tab functional offline)
    const fb = forecastFan(d, { history: 12, horizon: horizon * 3 });
    return { ...fb, company: "", companyName: "Synthetic (fallback)", model: "synthetic",
             source: "synthetic",
             accuracy: { qMAPE: null, annualMAPE: null, mae: null, rmse: null, mase: null, pi80Coverage: null },
             explain: null, available: false };
  }
  const key = opts.company && nf.companies[opts.company] ? opts.company : nf.meta.default_company;
  const c = nf.companies[key];
  const histN = c.actuals.length;
  const h = Math.min(horizon, c.forecast.length);

  return {
    labels: [...c.labels.slice(0, histN), ...c.labels.slice(histN, histN + h)],
    actuals: c.actuals,
    forecast: c.forecast.slice(0, h),
    fc50U: c.fc50U.slice(0, h), fc50L: c.fc50L.slice(0, h),
    fc80U: c.fc80U.slice(0, h), fc80L: c.fc80L.slice(0, h),
    fc95U: c.fc95U.slice(0, h), fc95L: c.fc95L.slice(0, h),
    d365: undefined,  // no baseline overlay — HWES forecast + PI bands speak for themselves
    company: c.ticker,
    companyName: c.name,
    model: nf.meta.model,
    source: nf.meta.source,
    accuracy: {
      qMAPE: c.accuracy.qMAPE,
      annualMAPE: c.accuracy.annual_MAPE,
      mae: c.accuracy.MAE ?? null,
      rmse: c.accuracy.RMSE ?? null,
      mase: c.accuracy.MASE ?? null,
      pi80Coverage: c.accuracy.pi80_coverage ?? null,
    },
    explain: c.explain ?? null,
    available: true,
  };
}

export function niForecastCompanies(d: Dataset): { key: string; name: string }[] {
  const nf = d.ni_forecast;
  if (!nf) return [];
  return Object.values(nf.companies).map((c) => ({ key: c.ticker, name: c.name }));
}

// ============================================================
// Aggregate over/underbilling by archetype as a divergence series
// ============================================================
export function overUnderByType(billing: BillingSummary[]) {
  const byType = billingByType(billing);
  return Array.from(byType.entries()).map(([k, v]) => ({
    label: prettyType(k),
    value: v.over + v.under,
    sub: `${v.n} proj · ${formatMoneyShort(v.revenue)} contract`,
  }));
}

export function prettyType(t: string): string {
  switch (t) {
    case "CLEAN_ROOM": return "Clean Room";
    case "COMM_INTERIORS": return "Comm Interiors";
    case "MEP": return "MEP";
    case "SCAFFOLD": return "Scaffold";
    case "OTHER": return "Insulation";
    default: return t;
  }
}

function formatMoneyShort(v: number): string {
  const a = Math.abs(v);
  if (a >= 1e9) return `$${(v / 1e9).toFixed(1)}B`;
  if (a >= 1e6) return `$${(v / 1e6).toFixed(0)}M`;
  if (a >= 1e3) return `$${(v / 1e3).toFixed(0)}K`;
  return `$${v.toFixed(0)}`;
}

// ============================================================
// DSO synthetic monthly trend (12m), per tier — anchored to the
// snapshot DSO numbers and shaped with mild trend + noise.
// ============================================================
export function dsoTrend(d: Dataset, months = 12) {
  const labels: string[] = [];
  const today = new Date(d.meta.asOf);
  for (let i = -months + 1; i <= 0; i++) {
    const dt = new Date(today.getFullYear(), today.getMonth() + i, 1);
    labels.push(`${dt.toLocaleString("en-US", { month: "short" })}'${String(dt.getFullYear()).slice(2)}`);
  }
  const direct: number[] = [];
  const ft: number[] = [];
  const sts: number[] = [];
  const dD = d.dso_by_tier.DIRECT;
  const dF = d.dso_by_tier.FIRST_TIER;
  const dS = d.dso_by_tier.SUB_TO_SUB;
  for (let i = 0; i < months; i++) {
    const drift = (i - months / 2) / months;
    direct.push(Math.max(20, dD + drift * 4 + Math.sin(i * 0.7) * 1.2));
    ft.push(Math.max(20, dF + drift * 6 + Math.sin(i * 0.9 + 1) * 1.6));
    sts.push(Math.max(20, dS + drift * 10 + Math.sin(i * 0.6 + 2) * 2.4));
  }
  return { months: labels, direct, ft, sts };
}

// ============================================================
// MSI sparkline series — derived from Clean Room share volatility.
// ============================================================
export function msiSeries(mix: PortfolioMix): number[] {
  const cr = mix.series.find((s) => s.name === "Clean Room")?.values ?? [];
  if (cr.length < 3) return cr.map(() => 0);
  // Rolling stdev × directional sign of |Δ|.
  const out: number[] = [];
  for (let i = 0; i < cr.length; i++) {
    const window = cr.slice(Math.max(0, i - 2), i + 1);
    const mean = window.reduce((a, b) => a + b, 0) / window.length;
    const sd = Math.sqrt(window.reduce((a, b) => a + (b - mean) ** 2, 0) / window.length);
    out.push(sd / 100); // proportion
  }
  return out;
}

// ============================================================
// CPI / SPI per project.
//
// CPI (Cost Performance Index) — derived from EAC drift: when current_eac
// > original_eac the project is forecast to come in above original budget,
// so CPI = original_eac / current_eac (< 1 = over budget).
//
// SPI (Schedule Performance Index) — time-based: ratio of actual % complete
// to scheduled % complete (where scheduled % is linear in elapsed days
// against planned start/end). Above 1 = ahead, below 1 = behind.
//
// Convention: ideal point is (CPI=1, SPI=1). Healthy band is ±1σ;
// out-of-control beyond ±2σ.
// ============================================================
export interface ProjectPerformance {
  project_id: string;
  project_type_code: string;
  branch_code: string;
  customer_account: string;
  tier: string;
  contract_value: number;
  cpi: number;
  spi: number;
  status: string;
  pct_complete: number;
  scheduled_pct: number;
  eac_drift_pct: number;
}

// Treat null, undefined, '', 'NaT', and 'None' (defensive against pandas/numpy
// sentinels leaking through serialisation) as "no actual end date".
function isMissingDate(v: unknown): boolean {
  if (v == null) return true;
  if (typeof v !== "string") return false;
  const s = v.trim();
  return s === "" || s === "NaT" || s === "None" || s === "null";
}

export function projectPerformance(d: Dataset): ProjectPerformance[] {
  const asOf = new Date(d.meta.asOf).getTime();
  const billingByProj = new Map(d.billing_summary.map((b) => [b.project_id, b]));
  return d.projects
    .filter((p) => p.status !== "Closed" && isMissingDate(p.actual_end_date))
    .map((p) => {
      const b = billingByProj.get(p.project_id);
      const startMs = new Date(p.planned_start_date).getTime();
      const endMs = new Date(p.planned_end_date).getTime();
      const totalDays = Math.max(1, (endMs - startMs) / 86_400_000);
      const elapsedDays = Math.max(0, (asOf - startMs) / 86_400_000);
      const scheduledPct = Math.max(0.001, Math.min(1, elapsedDays / totalDays));
      const actualPct = Math.max(0, b?.last_pct_complete ?? 0);

      // CPI = original / current EAC. eac_drift_pct is a fraction
      // (0.05 = 5% overrun, per synth/config.py docs).
      const driftFactor = 1 + (p.eac_drift_pct ?? 0);
      const cpi = driftFactor > 0 ? 1 / driftFactor : 1;

      // SPI = actual / scheduled.
      const spi = actualPct / scheduledPct;

      return {
        project_id: p.project_id,
        project_type_code: p.project_type_code,
        branch_code: p.branch_code,
        customer_account: p.customer_account,
        tier: p.tier,
        contract_value: p.current_contract_value,
        cpi,
        spi,
        status: p.status,
        pct_complete: actualPct,
        scheduled_pct: scheduledPct,
        eac_drift_pct: p.eac_drift_pct,
      };
    })
    .filter((r) => r.scheduled_pct > 0.05 && r.scheduled_pct < 1.5); // exclude not-started + long-overrun
}

export interface PortfolioPerformance {
  cpi: number;
  spi: number;
  inControlCount: number;
  watchCount: number;
  criticalCount: number;
  outliers: ProjectPerformance[];
}

// Portfolio-aggregated CPI / SPI weighted by contract value.
// Bands: in-control = ±1σ from (1,1); watch = 1-2σ; critical = >2σ.
// σ = 0.10 — typical for construction earned-value variance.
export function portfolioPerformance(perfs: ProjectPerformance[], sigma = 0.10): PortfolioPerformance {
  const total = perfs.reduce((s, p) => s + p.contract_value, 0) || 1;
  const cpi = perfs.reduce((s, p) => s + p.cpi * p.contract_value, 0) / total;
  const spi = perfs.reduce((s, p) => s + p.spi * p.contract_value, 0) / total;

  let inControl = 0, watch = 0, critical = 0;
  const outliers: ProjectPerformance[] = [];
  for (const p of perfs) {
    const dist = Math.sqrt((p.cpi - 1) ** 2 + (p.spi - 1) ** 2);
    if (dist <= sigma) inControl++;
    else if (dist <= 2 * sigma) watch++;
    else {
      critical++;
      outliers.push(p);
    }
  }
  outliers.sort((a, b) => {
    const da = Math.sqrt((a.cpi - 1) ** 2 + (a.spi - 1) ** 2);
    const db = Math.sqrt((b.cpi - 1) ** 2 + (b.spi - 1) ** 2);
    return db - da;
  });
  return { cpi, spi, inControlCount: inControl, watchCount: watch, criticalCount: critical, outliers };
}

// ============================================================
// Peer scatter points: % complete vs % billed for each project.
// ============================================================
export function peerScatter(billing: BillingSummary[]) {
  return billing
    .filter((b) => b.current_contract_value > 0)
    .map((b) => ({
      x: Math.max(0, Math.min(1, b.last_pct_complete)),
      y: Math.max(0, Math.min(1, b.billed_to_date / b.current_contract_value)),
      flagged: Math.abs((b.billed_to_date / b.current_contract_value) - b.last_pct_complete) > 0.18,
      label: b.project_id,
    }));
}

// ============================================================
// Hero KPI synthesis — pulls together the 8 board-level metrics.
// Some derive from real data; some are illustrative for the demo
// because we don't have GL / balance-sheet / covenant feeds wired.
// ============================================================
function smoothSeries(start: number, end: number, n: number, noise = 0.04, swing = 0.0): number[] {
  const out: number[] = [];
  for (let i = 0; i < n; i++) {
    const t = i / Math.max(1, n - 1);
    const linear = start + (end - start) * t;
    const wobble = Math.sin(i * 0.9) * swing;
    const jitter = (Math.sin(i * 3.7) + Math.cos(i * 1.3)) * noise;
    out.push(linear + wobble + jitter);
  }
  return out;
}

export function netIncomeQuarterly(d: Dataset): { value: number; ci: [number, number]; spark: number[] } {
  const totals = billingTotals(d.billing_summary);
  const monthlyRev = totals.revenue / 24;
  const margin = 0.085;
  const niQuarter = monthlyRev * 3 * margin;
  const ci: [number, number] = [niQuarter * 0.94, niQuarter * 1.06];
  // Spark: 16 trailing months of NI (smooth)
  const spark = smoothSeries(niQuarter * 0.78, niQuarter, 16, niQuarter * 0.015, niQuarter * 0.012);
  return { value: niQuarter, ci, spark };
}

export function cashNegProb(_d: Dataset): { value: number; spark: number[] } {
  // Illustrative: small, declining probability of cash falling below floor
  const spark = smoothSeries(4.4, 3.2, 16, 0.04, 0.05);
  return { value: 3.2, spark };
}

export function top10ArConcentration(d: Dataset): { value: number; spark: number[] } {
  const sorted = [...d.ar_by_customer].sort((a, b) => b.ar_balance - a.ar_balance);
  const top = sorted.slice(0, 10).reduce((a, c) => a + c.ar_balance, 0);
  const total = d.ar_by_customer.reduce((a, c) => a + c.ar_balance, 0);
  const pct = total > 0 ? (top / total) * 100 : 0;
  const spark = smoothSeries(pct - 5, pct, 16, 0.4, 0.3);
  return { value: pct, spark };
}

export function grossMarginTtm(_d: Dataset): { value: number; spark: number[] } {
  // Illustrative — not derivable from billing-only data
  const spark = smoothSeries(19.6, 18.4, 16, 0.05, 0.04);
  return { value: 18.4, spark };
}

export function netCashPosition(_d: Dataset): { value: number; spark: number[] } {
  // Illustrative — would tie to GL cash + ST debt in production
  const spark = smoothSeries(115, 142.6, 16, 0.6, 0.5);
  return { value: 142.6, spark };
}

// Net cash position trend + forward forecast, as a forecast fan (dollars).
// Anchored to the netCashPosition headline; production ties to GL cash − ST debt.
export function netCashFan(d: Dataset, opts: { history?: number; horizon?: number } = {}): FanData {
  const history = opts.history ?? 12;
  const horizon = opts.horizon ?? 6;
  const anchor = netCashPosition(d).value; // $M at end of history

  const labels: string[] = [];
  const today = new Date(d.meta.asOf);
  for (let i = -history + 1; i <= horizon; i++) {
    const dt = new Date(today.getFullYear(), today.getMonth() + i, 1);
    labels.push(`${dt.toLocaleString("en-US", { month: "short" })}'${String(dt.getFullYear()).slice(2)}`);
  }

  // Actuals: gentle rising trend ending at the anchor, with seasonality.
  const start = anchor * 0.80;
  const actuals: number[] = [];
  for (let i = 0; i < history; i++) {
    const t = i / Math.max(1, history - 1);
    const lin = start + (anchor - start) * t;
    const season = Math.sin(i * 0.7) * anchor * 0.018;
    const noise = (Math.sin(i * 2.3) + Math.cos(i * 1.1)) * anchor * 0.006;
    actuals.push((lin + season + noise) * 1e6);
  }
  actuals[actuals.length - 1] = anchor * 1e6; // pin the last actual to the headline

  // Forecast: continue at half the historical slope + seasonality.
  const last = actuals[actuals.length - 1];
  const monthlyDrift = ((anchor - start) / Math.max(1, history - 1)) * 0.5; // $M / month
  const forecast: number[] = [];
  for (let i = 0; i < horizon; i++) {
    const season = Math.sin((history + i) * 0.7) * anchor * 0.018;
    forecast.push(last + (monthlyDrift * (i + 1) + season) * 1e6);
  }

  // Prediction intervals widen with sqrt(t).
  const piPct = (i: number) => 0.035 + 0.012 * Math.sqrt(i + 1);
  const fc50U: number[] = [], fc50L: number[] = [];
  const fc80U: number[] = [], fc80L: number[] = [];
  const fc95U: number[] = [], fc95L: number[] = [];
  forecast.forEach((v, i) => {
    const w = piPct(i);
    fc50U.push(v * (1 + w * 0.4)); fc50L.push(v * (1 - w * 0.4));
    fc80U.push(v * (1 + w * 0.7)); fc80L.push(v * (1 - w * 0.7));
    fc95U.push(v * (1 + w));       fc95L.push(v * (1 - w));
  });

  return { labels, actuals, forecast, fc50U, fc50L, fc80U, fc80L, fc95U, fc95L };
}

export function dscr(_d: Dataset): { value: number; spark: number[] } {
  // Illustrative
  const spark = smoothSeries(2.55, 2.84, 16, 0.012, 0.008);
  return { value: 2.84, spark };
}

export function dsoDriftEnt(d: Dataset): { value: number; spark: number[] } {
  const dsoNow = dsoOverall(d.ar_by_customer);
  const drift = dsoNow - 60; // benchmark
  const spark = smoothSeries(drift - 1.5, drift, 16, 0.08, 0.08);
  return { value: drift, spark };
}

// ============================================================
// L1 KPI helpers
// ============================================================
export function underbillingPctRevenue(billing: BillingSummary[]): number {
  const totals = billingTotals(billing);
  return totals.revenue > 0 ? (Math.abs(totals.under) / totals.revenue) * 100 : 0;
}

export function mixShiftIndex(mix: PortfolioMix): number {
  // ½ Σ |share_t − share_t-12| — 12 here = ~last vs first quarter
  const n = mix.labels.length;
  if (n < 2) return 0;
  let sum = 0;
  for (const s of mix.series) {
    sum += Math.abs(s.values[n - 1] - s.values[0]);
  }
  return sum / 200; // /2 for the half, /100 to convert pp to share
}

export function nextQuarterOverUnder(d: Dataset): { value: number; ci: [number, number] } {
  const totals = billingTotals(d.billing_summary);
  // Modest mean-reversion forecast
  const v = totals.net * 0.78;
  const w = Math.abs(totals.net) * 0.18;
  return { value: v, ci: [v - w, v + w] };
}

// ============================================================
// L2 KPI helpers
// ============================================================
export function customersInCriticalTier(d: Dataset): number {
  // Use risk score from the watchlist logic
  const lookup = customerLookup(d.customers);
  return d.ar_by_customer.filter((a) => {
    const c = lookup.get(a.account_num);
    let s = 0;
    if (a.last_pay_cycle_days != null) {
      if (a.last_pay_cycle_days > 100) s += 35;
      else if (a.last_pay_cycle_days > 75) s += 22;
      else if (a.last_pay_cycle_days > 60) s += 12;
    }
    if (c?.credit_paydex_baseline != null) {
      if (c.credit_paydex_baseline < 60) s += 25;
      else if (c.credit_paydex_baseline < 70) s += 12;
    }
    for (const e of d.credit_events.filter((e) => e.customer_account === a.account_num)) {
      s += e.severity === "Critical" ? 14 : e.severity === "High" ? 9 : e.severity === "Medium" ? 4 : 1;
    }
    return s >= 75;
  }).length;
}

export function arOver90Pct(d: Dataset): number {
  const total = Object.values(d.ar_aging).reduce((a, v) => a + v, 0);
  const overdue = (d.ar_aging["91-120"] ?? 0) + (d.ar_aging["120+"] ?? 0);
  return total > 0 ? (overdue / total) * 100 : 0;
}

// ============================================================
// L3 KPI helpers
// ============================================================
export function quarterlyMissPct(): number {
  // Illustrative — would be |forecast - actual| / |actual| on holdouts
  return 6.4;
}
export function gmShrinkageQoQ(): number {
  // Illustrative — pp change quarter over quarter
  return -72; // basis points
}

// ============================================================
// Pay-cycle boxplots per tier (synthesized from JSON DSO).
// ============================================================
export function payCycleBoxplots(d: Dataset) {
  return [
    { tier: "Direct-to-owner", color: "var(--c-direct)",
      median: Math.round(d.dso_by_tier.DIRECT),
      q1: Math.round(d.dso_by_tier.DIRECT - 12),
      q3: Math.round(d.dso_by_tier.DIRECT + 14),
      min: Math.round(d.dso_by_tier.DIRECT - 22),
      max: Math.round(d.dso_by_tier.DIRECT + 24),
      baseline: Math.round(d.dso_by_tier.DIRECT - 4) },
    { tier: "First-tier sub", color: "var(--c-ft)",
      median: Math.round(d.dso_by_tier.FIRST_TIER),
      q1: Math.round(d.dso_by_tier.FIRST_TIER - 14),
      q3: Math.round(d.dso_by_tier.FIRST_TIER + 16),
      min: Math.round(d.dso_by_tier.FIRST_TIER - 26),
      max: Math.round(d.dso_by_tier.FIRST_TIER + 28),
      baseline: Math.round(d.dso_by_tier.FIRST_TIER - 6) },
    { tier: "Sub-to-a-sub", color: "var(--c-sts)",
      median: Math.round(d.dso_by_tier.SUB_TO_SUB),
      q1: Math.round(d.dso_by_tier.SUB_TO_SUB - 18),
      q3: Math.round(d.dso_by_tier.SUB_TO_SUB + 20),
      min: Math.round(d.dso_by_tier.SUB_TO_SUB - 32),
      max: Math.round(d.dso_by_tier.SUB_TO_SUB + 34),
      baseline: Math.round(d.dso_by_tier.SUB_TO_SUB - 8) },
  ];
}

// ============================================================
// Material commodity tiles (illustrative — feeds Portfolio Billing)
// ============================================================
export const materialTiles = [
  { id: "K-103", name: "PPI Cement",  unit: "index", latest: "349.8",  delta: "+1.4% m/m",  deltaDirection: "warn", flag: "WATCH",
    z: 1.4, spark: [330, 332, 334, 336, 338, 339, 340, 342, 343, 344, 346, 347, 348, 349, 349, 350] },
  { id: "K-104", name: "PPI Steel",   unit: "index", latest: "287.4",  delta: "-2.1% m/m",  deltaDirection: "good", flag: "OK",
    z: -0.6, spark: [301, 300, 297, 296, 294, 292, 291, 290, 290, 289, 288, 288, 288, 287, 287, 287] },
  { id: "K-105", name: "PPI Lumber",  unit: "index", latest: "412.3",  delta: "+0.7% m/m",  deltaDirection: "warn", flag: "OK",
    z: 0.3, spark: [404, 405, 406, 407, 407, 408, 408, 409, 409, 410, 411, 411, 411, 412, 412, 412] },
  { id: "K-106", name: "PPI Copper",  unit: "index", latest: "528.1",  delta: "+4.8% m/m",  deltaDirection: "bad",  flag: "SPIKE",
    z: 2.3, spark: [490, 493, 497, 500, 503, 506, 510, 514, 517, 520, 522, 524, 525, 526, 527, 528] },
];

// ============================================================
// Macro band tiles (covariates feeding the forecast model)
// ============================================================
export const macroBandTiles = [
  { id: "K-141", name: "AIA ABI · 9-12m lead", val: "52.4", trend: "+1.8", direction: "up",
    spark: [49.5, 49.8, 50.1, 50.4, 50.6, 50.8, 51.1, 51.3, 51.5, 51.7, 51.9, 52.0, 52.1, 52.2, 52.3, 52.4] },
  { id: "K-181", name: "BLS PPI · construction", val: "287.6", trend: "+0.4%", direction: "warn",
    spark: [281, 282, 283, 283, 284, 284, 285, 285, 286, 286, 286, 287, 287, 287, 287, 288] },
  { id: "K-190", name: "FRED 10Y Treasury", val: "4.32%", trend: "−7bp", direction: "down",
    spark: [4.45, 4.44, 4.42, 4.41, 4.40, 4.39, 4.38, 4.37, 4.36, 4.36, 4.35, 4.34, 4.33, 4.33, 4.32, 4.32] },
  { id: "K-192", name: "Dodge starts · 6mma", val: "+3.1%", trend: "vs PY", direction: "up",
    spark: [1.2, 1.4, 1.6, 1.8, 2.0, 2.2, 2.3, 2.4, 2.5, 2.6, 2.7, 2.8, 2.9, 3.0, 3.0, 3.1] },
];
